import { useContext, useEffect, useMemo, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'
import { ConfigContext } from 'contexts/ConfigContext'
import { Form, Row, Col } from 'react-bootstrap'
import objectPath from 'object-path'
import Label from './Label'
import { getCheckValue, getCurrentUi } from 'designer/component/action/Data'
import useAuth from 'hooks/useAuth'

const CustomCheckboxGroup = (props) => {
  let {
    data = {},
    uischema,
    updateCheckbox,
    updateInput,
    isDynamic,
    errors = {},
    uiKey,
    uiName,
    config,
    fieldName,
    fieldKey,
  } = props
  let { checkboxWidth, options = [], maxHeight } = uischema

  const { isArray, arrayName } = config

  const designContext = useContext(DesignContext)
  const { elementAction, setElementAction, isUiChanged, setFieldFormData } =
    designContext
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const auth = useAuth()
  const authConfig = useMemo(
    () => (Array.isArray(auth?.config) ? auth.config : []),
    [auth?.config]
  )
  const [render, setRender] = useState(false)

  const getConfigJsonData = (foundConfig) => {
    let data = foundConfig?.jsonData ?? foundConfig?.data ?? []
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data)
      } catch (error) {
        data = []
      }
    }
    return data || []
  }

  const getConfigData = () => {
    let { configCode, checkboxData } = uischema?.config || {}
    let foundConfig = authConfig.find(
      ({ configCode: itemConfigCode }) => itemConfigCode === configCode
    )
    if (!foundConfig) return []
    let data = getConfigJsonData(foundConfig)
    if (!checkboxData) return data
    return objectPath.get(data, checkboxData) || []
  }

  const getConfigCheckboxOptions = () => {
    let {
      checkboxName,
      checkboxLabel,
      valueChecked = true,
      valueUnchecked = false,
    } = uischema?.config || {}
    let checkboxDataList = getConfigData()
    if (!Array.isArray(checkboxDataList) || !checkboxName) return []
    return checkboxDataList.map((item, index) => {
      let name = objectPath.get(item, checkboxName)
      let label = checkboxLabel ? objectPath.get(item, checkboxLabel) : name
      name = name === undefined || name === null ? '' : `${name}`
      label = label === undefined || label === null ? '' : `${label}`
      return {
        key: name || `checkbox${index + 1}`,
        name: name || `checkbox${index + 1}`,
        label: label || name || '',
        value: '',
        formData: true,
        defaultChecked: false,
        valueChecked,
        valueUnchecked,
        '@value': item,
      }
    })
  }

  const [checkboxOptions, setCheckboxOptions] = useState(
    uischema.optionType === 'config' ? getConfigCheckboxOptions() : options
  )
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', uiKey])
  let isEnable = objectPath.get(elementAction, ['isEnable', uiKey])
  let updateCheckboxList =
    objectPath.get(elementAction, ['updateCheckboxList']) || []
  const uiChanged = objectPath(isUiChanged)
  let isCheckedAll = objectPath.get(data, '@isCheckedAll') || {}
  let checkedAll = isArray
    ? objectPath.get(isCheckedAll, `@${arrayName}.${uiName}`)
    : objectPath.get(isCheckedAll, uiName)

  const setIsCheckedAll = (isCheckAll) => {
    if (isArray) {
      setFieldFormData(`@isCheckedAll.@${arrayName}.${uiName}`, isCheckAll)
    } else {
      setFieldFormData(`@isCheckedAll.${uiName}`, isCheckAll)
    }
  }

  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  const styleChange = useMemo(() => {
    let style = {
      display: 'grid',
      width: checkboxWidth ? checkboxWidth : '23px',
      height: checkboxWidth ? checkboxWidth : '23px',
    }
    return style
  }, [checkboxWidth])

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    []
  )

  useEffect(() => {
    render && setRender(false)
  }, [render])

  useEffect(() => {
    if (uischema.optionType !== 'config') return
    let options = getConfigCheckboxOptions()
    if (JSON.stringify(options) !== JSON.stringify(checkboxOptions || [])) {
      setCheckboxOptions(options)
    }
  }, [
    uischema.optionType,
    uischema?.config?.configCode,
    uischema?.config?.checkboxData,
    uischema?.config?.checkboxName,
    uischema?.config?.checkboxLabel,
    uischema?.config?.valueChecked,
    uischema?.config?.valueUnchecked,
    authConfig,
  ])

  let checkboxUiChanged = uiChanged.get(`${uischema.key}`)
  let checkboxRender = uiChanged.get(`${uischema.key}.isRender`)

  useEffect(() => {
    if (checkboxRender) {
      let foundUpdate = updateCheckboxList.find((key) => key === uischema.key)
      if (!foundUpdate) return setCheckboxOptions(uischema?.options || [])
      let listKey = objectPath.get(elementAction, [
        'actionUpdateCheckbox',
        uischema.key,
      ])
      let selectList = uiChanged.get(`${listKey}.selectList`)
      let checkboxList = objectPath.get(selectList, uischema.name)
      let checkboxListAction =
        objectPath.get(checkboxUiChanged, 'updateCheckboxList') || {}
      let { checkboxLabel, checkboxName } =
        checkboxListAction.updateCheckboxList || checkboxListAction

      if (checkboxList && Array.isArray(checkboxList)) {
        let checkboxOptions = []
        checkboxList.forEach((item) => {
          checkboxOptions.push({
            key: objectPath.get(item, checkboxName),
            name: objectPath.get(item, checkboxName),
            label: objectPath.get(item, checkboxLabel),
            value: '',
            formData: true,
            valueChecked: true,
            defaultChecked: false,
            valueUnchecked: false,
          })
        })
        setCheckboxOptions(checkboxOptions)
      } else {
        setCheckboxOptions([])
      }

      let filterUpdateOption = updateCheckboxList.filter(
        (key) => key !== uischema.key
      )
      objectPath.set(elementAction, ['updateCheckboxList'], filterUpdateOption)
      setElementAction(elementAction)
    }
  }, [checkboxRender])

  const updateCheckAll = (checked, isOnChange) => {
    checkboxOptions.forEach((checkbox) => {
      let { valueChecked, valueUnchecked } = checkbox
      valueChecked = getCheckValue(valueChecked)
      valueUnchecked = getCheckValue(valueUnchecked)
      let value = checked ? valueChecked : valueUnchecked
      updateCheckbox(checkbox, value, isOnChange)
    })
    setRender(true)
    setIsCheckedAll(checked)
  }

  const updateCheckSingle = async (checkbox, checked, isOnChange) => {
    let { input = {}, valueChecked, valueUnchecked, enableInput } = checkbox
    valueChecked = getCheckValue(valueChecked)
    valueUnchecked = getCheckValue(valueUnchecked)
    let value = checked ? valueChecked : valueUnchecked
    updateCheckbox(checkbox, value, isOnChange)
    setRender(true)
    if (enableInput) {
      updateInput(checkbox, value ? input?.defaultValue : '', isOnChange)
    }
    let filterCheckAll = checkboxOptions.filter((option) => {
      let { name, valueChecked } = option
      name = arrayName ? `@${arrayName}.${name}` : name
      let checkboxValue = objectPath.get(data, name)
      valueChecked = getCheckValue(valueChecked)
      return checkboxValue === valueChecked
    })
    let isCheckAll = filterCheckAll.length === checkboxOptions.length
    setIsCheckedAll(isCheckAll)
  }

  return (
    <Form.Group className="mb-0" controlId={uischema.controlId}>
      <Row className="w-100 mx-0 d-flex align-items-center">
        <Label
          {...props}
          fieldKey={fieldKey}
          fieldName={fieldName}
          uischema={uischema}
          className={false}
          mt={2}
          isEnable={!isReadOnly}
        />
        <Col sm={uischema.elementWidth} className={`row w-100 mx-0 p-0`}>
          {uischema.isAll && (
            <Row>
              <Col sm="12" className="d-flex align-items-end">
                <Form.Check
                  style={{ ...styleChange, marginTop: '.10rem' }}
                  type="checkbox"
                  name="checkboxAll"
                  id="checkbox-all"
                  checked={checkedAll}
                  onChange={(e) => {
                    let checked = e.target.checked
                    updateCheckAll(checked, true)
                  }}
                  disabled={isReadOnly}
                />
                <Form.Label
                  className={`m-0 w-100 pl-2`}
                  style={{
                    color: colorByTheme,
                  }}
                  onClick={(e) => {
                    if (!isReadOnly) {
                      updateCheckAll(!checkedAll, true)
                    }
                  }}
                >
                  ทั้งหมด
                </Form.Label>
              </Col>
              <Col sm="12">
                <hr className="my-2" />
              </Col>
            </Row>
          )}
          <Row
            className={`mx-0 w-100 mt-0 d-flex align-items-start`}
            style={{ maxHeight: maxHeight, overflowY: 'auto' }}
          >
            {checkboxOptions.map((checkbox = {}, index) => {
              let {
                input = {},
                valueChecked,
                valueUnchecked,
                enableInput,
              } = checkbox

              let checked
              let isChecked

              valueChecked = getCheckValue(valueChecked)
              valueUnchecked = getCheckValue(valueUnchecked)

              let checkboxGroupName = uischema.name
              let checkboxKey = checkbox.key
              let checkboxName = checkbox.name

              let inputKey = input.key
              let inputName = input.name

              if (isArray) {
                checkboxGroupName = `@${arrayName}.${checkboxGroupName}`
                checkboxKey = `@${arrayName}.${checkboxKey}`
                checkboxName = `@${arrayName}.${checkboxName}`
                inputKey = `@${arrayName}.${inputKey}`
                inputName = `@${arrayName}.${inputName}`
              }

              if (uischema.valueType === 'text') {
                let checkboxGroupValue =
                  objectPath.get(data, checkboxGroupName) || ''
                let splitValue = checkboxGroupValue.split(
                  uischema?.joinValue || ''
                )
                let foundSplitValue = splitValue.find(
                  (value) => value === checkbox.name
                )
                checked = foundSplitValue ? true : false
              } else {
                checked =
                  objectPath.get(data, checkboxKey) ||
                  objectPath.get(data, checkboxName)
              }

              isChecked =
                checked === valueChecked ? valueChecked : valueUnchecked

              let inputValue =
                objectPath.get(data, inputKey) ||
                objectPath.get(data, inputName)

              return (
                <Col
                  sm={uischema.samePattern ? uischema.radioWidth || 'auto' : 12}
                  key={`checkbox-${uischema.key}-${index + 1}`}
                >
                  <Row className={`row mb-1`}>
                    <Col sm="auto" className="pl-0 d-flex align-items-end">
                      <Form.Check
                        style={{ ...styleChange, marginTop: '.10rem' }}
                        type="checkbox"
                        name={checkbox.name}
                        id={`checkbox-${checkbox.key}-${index + 1}`}
                        defaultChecked={checkbox.defaultChecked}
                        checked={isChecked}
                        onChange={(e) => {
                          let checked = e.target.checked
                          updateCheckSingle(checkbox, checked, true)
                        }}
                        disabled={isReadOnly || checkbox.readonly}
                      />
                      {checkbox?.label && (
                        <Form.Label
                          className={`m-0 w-100 pl-2`}
                          style={{
                            color: colorByTheme,
                          }}
                          onClick={(e) => {
                            if (!isReadOnly) {
                              let checked = !isChecked
                              updateCheckSingle(checkbox, checked, true)
                            }
                          }}
                        >
                          {checkbox.label}
                        </Form.Label>
                      )}
                    </Col>
                    {checkbox.enableInput && (
                      <Col sm="auto" className="pl-0">
                        <Form.Control
                          type="text"
                          name={input?.name}
                          id={'checkbox-' + input.key}
                          placeholder={input?.placeholder}
                          autoComplete="off"
                          defaultValue={
                            checked === valueChecked ? input?.defaultValue : ''
                          }
                          value={inputValue}
                          minLength={input?.minLength}
                          maxLength={input?.maxLength}
                          onChange={(e) =>
                            updateInput(checkbox, e.target.value, true)
                          }
                          readOnly={
                            isReadOnly ||
                            checked !== valueChecked ||
                            checkbox.readonly
                          }
                        />
                      </Col>
                    )}
                  </Row>
                </Col>
              )
            })}
            <Col sm="" className="text-danger">
              {checkboxOptions.map((checkbox = {}, index) => {
                let { input } = checkbox
                if (input?.name) {
                  let uiInputKey = isDynamic ? input.name : input.key
                  let errorCheckbox = objectPath.get(errors, uiInputKey) || []
                  let errorInput = objectPath.get(errors, uiInputKey) || []

                  let hasRequire = objectPath.has(elementAction, [
                    'isRequire',
                    input.key,
                  ])
                  let requireMessage = objectPath.get(elementAction, [
                    'isRequire',
                    input.key,
                  ])
                  let value = objectPath.get(data, uiInputKey)
                  let showRequireMessage = hasRequire
                    ? typeof requireMessage !== 'boolean'
                    : require
                  let showRequire = value ? false : hasRequire ? true : false

                  let hasWarning = objectPath.has(elementAction, [
                    'isWarning',
                    input.key,
                  ])
                  let warningMessage = objectPath.get(elementAction, [
                    'isWarning',
                    input.key,
                  ])
                  let showWarningMessage = hasWarning
                    ? typeof warningMessage !== 'boolean'
                    : warningMessage

                  return (
                    <ul
                      className="m-0 mt-2"
                      key={`error-${uiInputKey}-${index + 1}`}
                    >
                      {showRequire && showRequireMessage && requireMessage && (
                        <li className="text-danger">{requireMessage}</li>
                      )}
                      {showWarningMessage && warningMessage && (
                        <li className="text-danger">{warningMessage}</li>
                      )}
                      {errorInput.map((error, index) => (
                        <li
                          key={`error-checkinput-${uiInputKey}-${index + 1}`}
                          className="text-danger"
                        >
                          {error}
                        </li>
                      ))}
                      {errorCheckbox.map((error, index) => (
                        <li
                          key={`error-checkbox-${uiInputKey}-${index + 1}`}
                          className="text-danger"
                        >
                          {error}
                        </li>
                      ))}
                    </ul>
                  )
                }
                return ''
              })}
            </Col>
          </Row>
        </Col>
      </Row>
    </Form.Group>
  )
}

export default CustomCheckboxGroup
